import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronRight, Copy, Check, Brain, User, Sparkles, Database } from 'lucide-react';

/**
 * PromptPreviewModal - Admin-only modal for viewing the LLM prompt
 *
 * Features:
 * - Collapsible sections for system and user prompts
 * - Copy-to-clipboard buttons
 * - Student context summary
 * - Session insights visualization
 */
const PromptPreviewModal = ({ data, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({
    insights: true,
    student: true,
    user: true,
    system: false
  });
  const [copiedSection, setCopiedSection] = useState(null);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const copyToClipboard = async (text, section) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSection(section);
      setTimeout(() => setCopiedSection(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!data) return null;

  const { insights_summary, student_context, system_prompt, user_prompt, student_name } = data;

  return (
    <div className="sp-modal-overlay sp-prompt-modal-overlay" onClick={handleOverlayClick}>
      <div className="sp-modal sp-prompt-modal">
        {/* Header */}
        <div className="sp-modal__header sp-prompt-modal__header">
          <div className="sp-prompt-modal__title-group">
            <Brain size={20} className="sp-prompt-modal__icon" />
            <h3 className="sp-modal__title">LLM Prompt Preview</h3>
          </div>
          <span className="sp-prompt-modal__badge">Admin Only</span>
          <button className="sp-modal__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="sp-modal__body sp-prompt-modal__body">
          {/* Student Name Banner */}
          <div className="sp-prompt-banner">
            <User size={16} />
            <span>Generating plan for: <strong>{student_name || 'Unknown Student'}</strong></span>
          </div>

          {/* Session Insights Section */}
          <div className="sp-prompt-section">
            <button
              className="sp-prompt-section__header"
              onClick={() => toggleSection('insights')}
            >
              <span className="sp-prompt-section__toggle">
                {expandedSections.insights ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
              <Database size={16} />
              <span className="sp-prompt-section__title">Session Insights</span>
              <span className="sp-prompt-section__count">
                {insights_summary?.total_sessions || 0} sessions analyzed
              </span>
            </button>
            {expandedSections.insights && (
              <div className="sp-prompt-section__content sp-prompt-insights">
                <div className="sp-prompt-insights__grid">
                  <div className="sp-prompt-insights__item">
                    <h5>Successful Methods</h5>
                    {insights_summary?.successful_methods?.length > 0 ? (
                      <ul>
                        {insights_summary.successful_methods.map((method, i) => (
                          <li key={i}>{method}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="sp-prompt-empty">No methods recorded</p>
                    )}
                  </div>
                  <div className="sp-prompt-insights__item">
                    <h5>Current Challenges</h5>
                    {insights_summary?.current_challenges?.length > 0 ? (
                      <ul>
                        {insights_summary.current_challenges.map((challenge, i) => (
                          <li key={i}>{challenge}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="sp-prompt-empty">No challenges identified</p>
                    )}
                  </div>
                  <div className="sp-prompt-insights__item">
                    <h5>Recommended Next Steps</h5>
                    {insights_summary?.recommended_next_steps?.length > 0 ? (
                      <ul>
                        {insights_summary.recommended_next_steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="sp-prompt-empty">No next steps recommended</p>
                    )}
                  </div>
                  <div className="sp-prompt-insights__item">
                    <h5>Recent Topics</h5>
                    {insights_summary?.recent_topics?.length > 0 ? (
                      <div className="sp-prompt-topics">
                        {insights_summary.recent_topics.map((topic, i) => (
                          <span key={i} className="sp-prompt-topic-tag">{topic}</span>
                        ))}
                      </div>
                    ) : (
                      <p className="sp-prompt-empty">No topics recorded</p>
                    )}
                  </div>
                </div>
                <div className="sp-prompt-insights__source">
                  Data source: <code>{insights_summary?.data_source || 'unknown'}</code>
                </div>
              </div>
            )}
          </div>

          {/* Student Context Section */}
          <div className="sp-prompt-section">
            <button
              className="sp-prompt-section__header"
              onClick={() => toggleSection('student')}
            >
              <span className="sp-prompt-section__toggle">
                {expandedSections.student ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
              <User size={16} />
              <span className="sp-prompt-section__title">Student Profile</span>
            </button>
            {expandedSections.student && (
              <div className="sp-prompt-section__content sp-prompt-student">
                <div className="sp-prompt-student__grid">
                  <div className="sp-prompt-student__field">
                    <span className="sp-prompt-student__label">Grade</span>
                    <span className="sp-prompt-student__value">{student_context?.grade || 'Not set'}</span>
                  </div>
                  <div className="sp-prompt-student__field">
                    <span className="sp-prompt-student__label">Learning Style</span>
                    <span className="sp-prompt-student__value">{student_context?.learning_style || 'Not set'}</span>
                  </div>
                  <div className="sp-prompt-student__field">
                    <span className="sp-prompt-student__label">Subjects</span>
                    <span className="sp-prompt-student__value">{student_context?.target_subjects || 'Not set'}</span>
                  </div>
                  <div className="sp-prompt-student__field">
                    <span className="sp-prompt-student__label">Interests</span>
                    <span className="sp-prompt-student__value">{student_context?.interests || 'Not set'}</span>
                  </div>
                  <div className="sp-prompt-student__field sp-prompt-student__field--full">
                    <span className="sp-prompt-student__label">Goals</span>
                    <span className="sp-prompt-student__value">{student_context?.goals || 'Not set'}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* User Prompt Section */}
          <div className="sp-prompt-section">
            <button
              className="sp-prompt-section__header"
              onClick={() => toggleSection('user')}
            >
              <span className="sp-prompt-section__toggle">
                {expandedSections.user ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
              <Sparkles size={16} />
              <span className="sp-prompt-section__title">User Prompt</span>
              <span className="sp-prompt-section__hint">Contains student-specific data</span>
            </button>
            {expandedSections.user && (
              <div className="sp-prompt-section__content">
                <div className="sp-prompt-code-header">
                  <button
                    className="sp-prompt-copy-btn"
                    onClick={() => copyToClipboard(user_prompt, 'user')}
                  >
                    {copiedSection === 'user' ? (
                      <><Check size={14} /> Copied</>
                    ) : (
                      <><Copy size={14} /> Copy</>
                    )}
                  </button>
                </div>
                <pre className="sp-prompt-code">{user_prompt}</pre>
              </div>
            )}
          </div>

          {/* System Prompt Section */}
          <div className="sp-prompt-section">
            <button
              className="sp-prompt-section__header"
              onClick={() => toggleSection('system')}
            >
              <span className="sp-prompt-section__toggle">
                {expandedSections.system ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
              <Brain size={16} />
              <span className="sp-prompt-section__title">System Prompt</span>
              <span className="sp-prompt-section__hint">LRB Neuro-Informed Framework</span>
            </button>
            {expandedSections.system && (
              <div className="sp-prompt-section__content">
                <div className="sp-prompt-code-header">
                  <button
                    className="sp-prompt-copy-btn"
                    onClick={() => copyToClipboard(system_prompt, 'system')}
                  >
                    {copiedSection === 'system' ? (
                      <><Check size={14} /> Copied</>
                    ) : (
                      <><Copy size={14} /> Copy</>
                    )}
                  </button>
                </div>
                <pre className="sp-prompt-code">{system_prompt}</pre>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sp-modal__footer sp-prompt-modal__footer">
          <button
            type="button"
            className="sp-btn sp-btn--outline"
            onClick={() => copyToClipboard(`${system_prompt}\n\n---\n\n${user_prompt}`, 'full')}
          >
            {copiedSection === 'full' ? (
              <><Check size={14} /> Copied Full Prompt</>
            ) : (
              <><Copy size={14} /> Copy Full Prompt</>
            )}
          </button>
          <button
            type="button"
            className="sp-btn sp-btn--primary"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptPreviewModal;
